<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/Creating_your_Metamodel';
    let nextLink= '/Tutorial/More_Fun_with_Projections';
    
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="making-an-editor-for-your-dsl-1"
  bind:intersecting={visibleById["making-an-editor-for-your-dsl-1"]}
  onIntersectChange={(visible) => setVisible("making-an-editor-for-your-dsl-1", visible)}
>Making an Editor for your DSL</SectionComponent>
<p>At last, your patience is rewarded. In this step of the tutorial we’re going to make the editor look great!</p>
<SectionComponent tag="h2" 
  id="the-default-editor-2"
  bind:intersecting={visibleById["the-default-editor-2"]}
  onIntersectChange={(visible) => setVisible("the-default-editor-2", visible)}
>The default editor</SectionComponent>
<p>Let’s create a file called <code>edu-main.edit</code> in the <code>src/defs</code> folder,
and define a default editor for this model unit. Please enter the following line.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L3-L3

editor default</code></pre>
<p>There—that’s done!</p>
<p>No, of course we are not done. Just kidding—and showing you that Freon will add a default <a
  href="/Documentation/Defining_an_Editor/Projections"
>projection</a> for any concept that is not
included in the editor definition. You can build your editor concept by concept.</p>
<p>In fact, the editor that is generated in Lesson 1 is completely based on the defaults generated by Freon.</p>
<Figure
imageName={'tutorial/Tutorial-lesson2-screenshot1.png'}
caption={'Editor from lesson 1'}
figureNumber={2}
/>
<SectionComponent tag="h2" 
  id="a-projection-for-the-topic-model-unit-3"
  bind:intersecting={visibleById["a-projection-for-the-topic-model-unit-3"]}
  onIntersectChange={(visible) => setVisible("a-projection-for-the-topic-model-unit-3", visible)}
>A projection for the <em>Topic</em> model unit</SectionComponent>
<p>A complete editor definition is built from a number of projections. To define a single projection
we need the name of the concept, some curly brackets, and—last but not least—a pair of square brackets. Everything
that goes between the square brackets will be taken as literally as possible in the editor. That is,
if there is indentation or literal text, this will be projected as unchangeable strings.</p>
<p>The first step is to focus on the <em>Topic</em> model unit. Let’s state how this model unit should look.
The projection for <em>Topic</em> consists of four parts.</p>
<ul>
<li>A line with the fixed
string <code>SiteGroup:</code> followed by the name of the site group. For the latter we use a syntax similar to the smart
strings in TypeScript: <code>$&#123;self.main&#125;</code>. You may leave out the <code>self.</code> part. It indicates that we refer to the property of the <em>Topic</em> object
that is projected. For clarity, we will use this prefix throughout the tutorial.</li>
<li>A line with the fixed string <code>Topic:</code> followed by the name of the unit.</li>
<li>The third line is similar, but projects the <code>description</code> property of the model unit. Next, we add an empty line
which—like the indentation—will show in the editor.</li>
<li>The last part of the definition gives the projection of the <code>pages</code> property, which is a list. By adding the keyword <code>vertical</code>
we tell Freon to project the list vertically.</li>
</ul>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L3-L16

editor default

Topic &#123;[
    SiteGroup: $&#123;self.main&#125;
    Topic: $&#123;self.name&#125;
    Topic description: $&#123;self.description&#125;

    Pages:
    $&#123;self.pages vertical &#125;
]&#125;

Page &#123;[
    $&#123;self.name&#125;
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-possibilities-for-lists-4"
  bind:intersecting={visibleById["the-possibilities-for-lists-4"]}
  onIntersectChange={(visible) => setVisible("the-possibilities-for-lists-4", visible)}
>The Possibilities for Lists</SectionComponent>
<p>Above, we projected the pages as a vertical list. There are many options to project lists. Not all lists need to be
projected vertically—use the keyword <code>horizontal</code> to display all list elements on a single line.
The keyword may also be omitted; the default value is <code>vertical</code>.</p>
<p>In the next projection for the <em>Theory</em> concept, we project two lists: <code>self.content</code> and <code>self.questions</code>.
Notice that we added something to the projection of <code>self.content</code> in the
definition above: <code>terminator[== END OF LINE]</code>. It means that
after every element of the list the string <code>== END OF LINE</code> is projected.
Note that the line of dashes will also appear in the editor.</p>
<pre class="language-freon"><code class="language-freon">Theory &#123;[
    ----------------------------------------------------
    Theory
        $&#123;self.content vertical terminator[== END OF LINE]&#125;

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;</code></pre>
<p>Instead of using a <code>terminator</code>, you could also opt for a <code>separator</code>, where the character
or string is projected between every element of the list, or an <code>initiator</code>, which projects
the character or string before each element.</p>
<p>Here are some examples of how you can tweak the display.</p>
<pre class="language-freon"><code class="language-freon">    Theory
        $&#123;self.content vertical separator [.]&#125;

    Theory
        $&#123;self.content horizontal terminator [====]&#125;

    Theory
        $&#123;self.content vertical initiator [Line]&#125;</code></pre>
<p>Go ahead and try the different options.</p>
<SectionComponent tag="h2" 
  id="inherited-projections-5"
  bind:intersecting={visibleById["inherited-projections-5"]}
  onIntersectChange={(visible) => setVisible("inherited-projections-5", visible)}
>Inherited Projections</SectionComponent>
<p>Remember that we defined the concept <code>Page</code> to be abstract, and there were several concepts that inherit from <code>Page</code>? Of course,
we can build inherited projection definitions as well. We have defined the projection for the abstract concept <code>Page</code> as follows.
It’s nothing fancy, but you could do more if you like.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L14-L16

Page &#123;[
    $&#123;self.name&#125;
]&#125;</code></pre>
<p>Now look at how we incorporate this projection in the projection of one of <code>Page</code>’s children using this syntax: <code>[=&gt;Page]</code>. It means
that we include the default projection of <em>Page</em> right there.
Note that we can use inherited properties, like <code>questions</code>, as expected in the projection.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L18-L25

Theory &#123;[
    ----------------------------------------------------
    Theory [=&gt;Page]
        $&#123;self.content vertical terminator[== END OF LINE]&#125;

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;</code></pre>
<p>Now we can almost finish the projection for this model unit by adding the following lines. Each concept
that inherits from <code>Page</code> is defined, as well as the <code>questions</code> and <code>content</code> parts of <code>Page</code>.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L27-L76

Video &#123;[
    ----------------------------------------------------
    Video [=&gt;Page]
        Maybe this video will help you understand.
        $&#123;self.url&#125;

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;

WorkSheet &#123;[
    ----------------------------------------------------
    Worksheet [=&gt;Page]
        See if you can answer the following questions.

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;

ExamplePage &#123;[
    ----------------------------------------------------
    Example [=&gt;Page]
        $&#123;self.content&#125;

        Now, please, answer the following questions.

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;

InDepthMaterial &#123;[
    ----------------------------------------------------
    InDepthMaterial [=&gt;Page]
        $&#123;self.content&#125;

        Test your understanding by answering the following questions.

    Questions:
        $&#123;self.questions vertical&#125;
]&#125;

Question &#123;[
    $&#123;self.name&#125;
        $&#123;self.content&#125;
        Correct Answer: $&#123;self.correctAnswer&#125;
]&#125;

Line &#123;
    [$&#123;self.content&#125;]
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="triggers-6"
  bind:intersecting={visibleById["triggers-6"]}
  onIntersectChange={(visible) => setVisible("triggers-6", visible)}
>Triggers</SectionComponent>
<p>The last thing to learn in this lesson is how we can make editing easier for the user. Remember that we needed a concept for numeric fractions?
It’s called <code>Fraction</code>, and it is formed by combining two numbers: a numerator and a denominator. This is how the concept is defined in the <code>.ast</code> file.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.ast#L53-L56

concept Fraction base NumberConcept &#123;
    numerator: number;
    denominator: number;
&#125;</code></pre>
<p>In Freon, the user must first choose the <code>Fraction</code> option from a dropdown menu before they can enter any numbers. To avoid
this extra step, we can tell Freon that when a certain key or string is entered, it should automatically create an instance of
the associated concept. The key or string to be entered is called the <strong>trigger</strong>.</p>
<p>For the <code>Fraction</code> concept we define the trigger to be a forward slash. If the user wants to add a correct answer to a question,
where the possibilities are either a <code>SimpleNumber</code> or a <code>Fraction</code>, entering <code>/</code> will produce a <code>Fraction</code> instance. So, the
last two projections are defined as follows.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson2-defs/edu-topics.edit#L78-L85

SimpleNumber &#123;
    [$&#123;self.value&#125;]
&#125;

Fraction &#123;
    [$&#123;numerator&#125; / $&#123;denominator&#125;]
    trigger = "/"
&#125;</code></pre>
<p>Now go ahead, generate a new editor, and have a look. Your editor should now look like this—much better than the result from Lesson 1!</p>
<Figure
imageName={'tutorial/Tutorial-lesson2-screenshot2.png'}
caption={'Editor after adding some projection definitions'}
figureNumber={3}
/>
<p>When you’ve gotten this far, it might be a good idea to try for yourself what
happens if you change the projection—for instance, switching the order of the lines.
But don’t forget to get back to
the next part of the tutorial, where we’ll have more fun with defining projections.</p>


<PrevNextSection {prevLink} {nextLink} />